import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { applyThemeColors } from '../theme/colors';
import {
  getThemedPalette,
  resolveThemeMode,
  type AppColors,
  type ThemeMode,
} from '../theme/palettes';
import { isAccentColorId, type AccentColorId } from '../theme/accentColors';
import { Platform, TextInput } from 'react-native';

const MODE_STORAGE_KEY = '@goalmint/theme_mode';
const ACCENT_STORAGE_KEY = '@goalmint/accent_color';

type ThemeContextValue = {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  accentColor: AccentColorId;
  colors: AppColors;
  setMode: (mode: ThemeMode) => Promise<void>;
  setAccentColor: (accent: AccentColorId) => Promise<void>;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>('light');
  const [accentColor, setAccentColorState] = useState<AccentColorId>('default');

  useEffect(() => {
    AsyncStorage.getItem(MODE_STORAGE_KEY).then((stored) => {
      if (stored === 'light' || stored === 'dark') {
        setModeState(stored);
      } else if (stored === 'system') {
        setModeState('light');
        AsyncStorage.setItem(MODE_STORAGE_KEY, 'light');
      }
    });
  }, []);

  useEffect(() => {
    AsyncStorage.getItem(ACCENT_STORAGE_KEY).then((stored) => {
      if (stored && isAccentColorId(stored)) {
        setAccentColorState(stored);
      }
    });
  }, []);

  const resolvedMode = resolveThemeMode(mode);

  const palette = useMemo(
    () => getThemedPalette(resolvedMode, accentColor),
    [resolvedMode, accentColor]
  );

  useEffect(() => {
    applyThemeColors(palette);
    const existing = TextInput.defaultProps ?? {};
    TextInput.defaultProps = {
      ...existing,
      placeholderTextColor: palette.text.placeholder,
    };
  }, [palette]);

  const setMode = useCallback(async (next: ThemeMode) => {
    setModeState(next);
    await AsyncStorage.setItem(MODE_STORAGE_KEY, next);
  }, []);

  const setAccentColor = useCallback(async (next: AccentColorId) => {
    setAccentColorState(next);
    await AsyncStorage.setItem(ACCENT_STORAGE_KEY, next);
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', resolvedMode);
    }
  }, [resolvedMode]);

  const value = useMemo(
    () => ({
      mode,
      resolvedMode,
      accentColor,
      colors: palette,
      setMode,
      setAccentColor,
    }),
    [mode, resolvedMode, accentColor, palette, setMode, setAccentColor]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
};

/** Use when ThemeProvider may not wrap (e.g. tests); falls back to light palette. */
export const useThemeColors = (): AppColors => {
  const ctx = useContext(ThemeContext);
  return ctx?.colors ?? getThemedPalette('light', 'default');
};
